/**
 * Department runtime UI contracts — shared between chaos-gateway and chaos-ui.
 *
 * Maps the FastAPI surface of ``services/department-runtime`` into wire-shape
 * Zod schemas. The upstream service owns the standing queues, handoff
 * contracts, escalation rules and per-department workflow lifecycle; the
 * gateway is a thin auth + audit layer in front of it.
 *
 * Naming: the upstream uses snake_case (``department_key`` / ``oldest_pending_age_seconds``)
 * and the gateway serialises to camelCase at the API boundary. That keeps
 * the TS surface idiomatic without polluting the Python schemas.
 */
import { z } from 'zod';

// ── Health (traffic-light over a department) ──────────────────────────
// Mirrors ``DepartmentHealthState`` enum in
// services/department-runtime/src/department_runtime/schemas/department.py.
// We also expose a UI-facing string form ("healthy" / "lagging" / "failed"
// / "unknown") so the components don't have to translate at render time.
export const departmentHealthStates = ['green', 'yellow', 'red', 'unknown'] as const;
export const departmentHealthStateSchema = z.enum(departmentHealthStates);
export type DepartmentHealthState = z.infer<typeof departmentHealthStateSchema>;

export const departmentHealthLabels = ['healthy', 'lagging', 'failed', 'unknown'] as const;
export const departmentHealthLabelSchema = z.enum(departmentHealthLabels);
export type DepartmentHealthLabel = z.infer<typeof departmentHealthLabelSchema>;

/** Stable mapping from upstream traffic-light → UI label. */
export const HEALTH_STATE_TO_LABEL: Record<DepartmentHealthState, DepartmentHealthLabel> = {
  green: 'healthy',
  yellow: 'lagging',
  red: 'failed',
  unknown: 'unknown',
};

// ── Standing-queue item statuses ──────────────────────────────────────
export const queueItemStatuses = [
  'pending',
  'in_progress',
  'done',
  'error',
  'timeout',
  'cancelled',
] as const;
export const queueItemStatusSchema = z.enum(queueItemStatuses);
export type QueueItemStatus = z.infer<typeof queueItemStatusSchema>;

export const queueItemPriorities = ['urgent', 'high', 'normal', 'low'] as const;
export const queueItemPrioritySchema = z.enum(queueItemPriorities);
export type QueueItemPriority = z.infer<typeof queueItemPrioritySchema>;

// ── Department summary (list view) ────────────────────────────────────
// One row of the 21-cell grid on /departments. Carries the steward
// identity inline so the card renders without an extra fetch.
export const departmentSummarySchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  displayName: z.string(),
  mission: z.string().nullable(),
  roleDescription: z.string().nullable(),
  parentDepartmentId: z.string().uuid().nullable(),
  position: z.number().int().nullable(),
  isSystem: z.boolean(),
  workflowRunning: z.boolean(),
  workflowRunId: z.string().nullable(),
  cadenceSeconds: z.number().int().positive(),
  // Health rollup
  health: departmentHealthStateSchema,
  healthLabel: departmentHealthLabelSchema,
  pendingItems: z.number().int().nonnegative(),
  inProgressItems: z.number().int().nonnegative(),
  oldestPendingAgeSeconds: z.number().int().nonnegative(),
  throughput24h: z.number().int().nonnegative(),
  errorRate1h: z.number().min(0).max(1),
  lastProcessedAt: z.string().datetime().nullable(),
  // Reasons surfaced for tooltips / detail page header.
  reasons: z.array(z.string()),
});
export type DepartmentSummary = z.infer<typeof departmentSummarySchema>;

export const departmentSummaryListSchema = z.object({
  departments: z.array(departmentSummarySchema),
  total: z.number().int().nonnegative(),
});
export type DepartmentSummaryList = z.infer<typeof departmentSummaryListSchema>;

// ── Department detail (per-department page) ───────────────────────────
// Extends the summary with steward identity + standing queue counts.
export const departmentDetailSchema = departmentSummarySchema.extend({
  defaultSkillKeys: z.array(z.string()),
  headUserId: z.string().uuid().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type DepartmentDetail = z.infer<typeof departmentDetailSchema>;

// ── Standing-queue item (paginated) ───────────────────────────────────
export const queueItemSchema = z.object({
  id: z.string().uuid(),
  departmentKey: z.string(),
  departmentId: z.string().uuid(),
  title: z.string(),
  payload: z.unknown(),
  priority: queueItemPrioritySchema,
  status: queueItemStatusSchema,
  idempotencyKey: z.string().nullable(),
  correlationId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  startedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
  errorMessage: z.string().nullable(),
});
export type QueueItem = z.infer<typeof queueItemSchema>;

export const queuePageSchema = z.object({
  items: z.array(queueItemSchema),
  total: z.number().int().nonnegative(),
  nextCursor: z.string().nullable(),
});
export type QueuePage = z.infer<typeof queuePageSchema>;

// ── Handoff ───────────────────────────────────────────────────────────
export const handoffStatuses = [
  'requested',
  'accepted',
  'rejected',
  'processing',
  'done',
  'error',
  'timeout',
] as const;
export const handoffStatusSchema = z.enum(handoffStatuses);
export type HandoffStatus = z.infer<typeof handoffStatusSchema>;

export const handoffOutcomes = ['accepted', 'rejected', 'error'] as const;
export const handoffOutcomeSchema = z.enum(handoffOutcomes);
export type HandoffOutcome = z.infer<typeof handoffOutcomeSchema>;

export const handoffSchema = z.object({
  id: z.string().uuid(),
  source: z.string(),
  target: z.string(),
  contractName: z.string(),
  status: handoffStatusSchema,
  title: z.string(),
  payload: z.unknown(),
  correlationId: z.string().uuid().nullable(),
  queueItemId: z.string().uuid().nullable(),
  slaSeconds: z.number().int().positive().nullable(),
  slaDeadline: z.string().datetime().nullable(),
  requestedByUserId: z.string().uuid().nullable(),
  decidedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  rejectionReason: z.string().nullable(),
  outcome: handoffOutcomeSchema.nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Handoff = z.infer<typeof handoffSchema>;

export const handoffContractSchema = z.object({
  source: z.string(),
  target: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  requiredPayloadKeys: z.array(z.string()),
  optionalPayloadKeys: z.array(z.string()),
  slaSeconds: z.number().int().positive().nullable(),
  onTimeoutEscalateTo: z.array(z.string()),
  requiresApprovalRole: z.enum(['founder', 'department_head', 'none']),
});
export type HandoffContract = z.infer<typeof handoffContractSchema>;

export const handoffRequestSchema = z.object({
  source: z.string().min(1, 'Source department is required.'),
  target: z.string().min(1, 'Target department is required.'),
  contractName: z.string().min(1, 'Contract is required.'),
  title: z.string().min(1, 'Title is required.').max(512),
  payload: z.record(z.string(), z.unknown()).default({}),
  correlationId: z.string().uuid().nullable().optional(),
  priority: queueItemPrioritySchema.default('normal'),
  idempotencyKey: z.string().max(128).optional(),
  slaOverrideSeconds: z.number().int().positive().optional(),
});
export type HandoffRequest = z.infer<typeof handoffRequestSchema>;

// ── Escalation ────────────────────────────────────────────────────────
export const escalationSeverities = ['info', 'warn', 'critical'] as const;
export const escalationSeveritySchema = z.enum(escalationSeverities);
export type EscalationSeverity = z.infer<typeof escalationSeveritySchema>;

export const escalationStatuses = ['open', 'acknowledged', 'resolved', 'dismissed'] as const;
export const escalationStatusSchema = z.enum(escalationStatuses);
export type EscalationStatus = z.infer<typeof escalationStatusSchema>;

export const escalationSchema = z.object({
  id: z.string().uuid(),
  departmentKey: z.string(),
  departmentId: z.string().uuid(),
  severity: escalationSeveritySchema,
  status: escalationStatusSchema,
  title: z.string(),
  description: z.string().nullable(),
  correlationId: z.string().uuid().nullable(),
  suggestedOwnerRole: z.string().nullable(),
  payload: z.unknown(),
  openedByUserId: z.string().uuid().nullable(),
  acknowledgedAt: z.string().datetime().nullable(),
  acknowledgedByUserId: z.string().uuid().nullable(),
  resolvedAt: z.string().datetime().nullable(),
  resolvedByUserId: z.string().uuid().nullable(),
  resolutionNote: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Escalation = z.infer<typeof escalationSchema>;

export const escalationRequestSchema = z.object({
  departmentKey: z.string().min(1, 'Department is required.'),
  severity: escalationSeveritySchema.default('warn'),
  title: z.string().min(1, 'Title is required.').max(512),
  description: z.string().max(8_000).optional().nullable(),
  correlationId: z.string().uuid().nullable().optional(),
  suggestedOwnerRole: z.string().max(64).optional().nullable(),
  payload: z.record(z.string(), z.unknown()).default({}),
});
export type EscalationRequest = z.infer<typeof escalationRequestSchema>;

// ── Recent activity (used on overview tab) ────────────────────────────
// A union of queue / handoff / escalation events the UI shows on the
// department overview timeline. Each entry carries its own kind so the
// component can dispatch on it.
export const activityKinds = [
  'queue_item',
  'handoff_in',
  'handoff_out',
  'escalation_opened',
  'escalation_resolved',
] as const;
export const activityKindSchema = z.enum(activityKinds);
export type DepartmentActivityKind = z.infer<typeof activityKindSchema>;

export const departmentActivitySchema = z.object({
  kind: activityKindSchema,
  id: z.string().uuid(),
  title: z.string(),
  detail: z.string().nullable(),
  status: z.string(),
  occurredAt: z.string().datetime(),
});
export type DepartmentActivity = z.infer<typeof departmentActivitySchema>;

// ── WebSocket event (forwarded by /ws/departments/:deptId/events) ─────
// Postgres NOTIFY payload published by the migration 026 trigger on
// inserts/updates of standing_queues, department_handoffs and
// department_escalations.
export const departmentWsEventSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('queue_item'),
    event: z.enum(['INSERT', 'UPDATE']),
    departmentKey: z.string(),
    itemId: z.string().uuid(),
    status: queueItemStatusSchema,
    priority: queueItemPrioritySchema.nullable().optional(),
  }),
  z.object({
    kind: z.literal('handoff'),
    event: z.enum(['INSERT', 'UPDATE']),
    handoffId: z.string().uuid(),
    source: z.string(),
    target: z.string(),
    status: handoffStatusSchema,
  }),
  z.object({
    kind: z.literal('escalation'),
    event: z.enum(['INSERT', 'UPDATE']),
    escalationId: z.string().uuid(),
    departmentKey: z.string(),
    severity: escalationSeveritySchema,
    status: escalationStatusSchema,
  }),
  z.object({ kind: z.literal('hello'), departmentKey: z.string() }),
  z.object({ kind: z.literal('ping') }),
]);
export type DepartmentWsEvent = z.infer<typeof departmentWsEventSchema>;
